import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { WidgetAPI } from "@/context/widget-context";
import { cn, Language, useTranslations } from "@/lib";
import { Plus } from "lucide-react";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
export function CushionRecommendations({
  lang,
  widget,
}: {
  widget: WidgetAPI;
  lang: Language;
}) {
  const { t } = useTranslations(lang);
  const cushionRecommendations = widget.products
    .flatMap((product) => product.cushionRecommendations || [])
    .reduce(
      (acc, curr) => {
        const exists = acc.find((item) => item.id === curr.id);
        if (!exists) {
          acc.push(curr);
        }
        return acc;
      },
      [] as {
        id: string;
        title: string;
        url: string;
        name: string;
        imageUrl: string;
        variant: string;
        price: string;
        originalPrice: string;
        size: string;
      }[],
    )
    .slice(0, 4);
  return (
    <Drawer open={true}>
      <DrawerContent className="tw:h-[94dvh] tw:!max-h-[94dvh] tw:!mt-1 tw:sm:max-w-lg tw:ml-auto">
        <DrawerHeader>
          <DrawerTitle className="tw:font-pp-fragment tw:text-2xl tw:font-medium tw:text-center">
            {t("cushion_recommendations.title")}
          </DrawerTitle>
        </DrawerHeader>
        <ScrollArea className="tw:md:h-[70dvh] tw:h-[calc(100dvh_-_240px)]">
          <div className="tw:grid tw:grid-cols-2 tw:mt-4 tw:md:w-lg tw:mx-auto">
            {cushionRecommendations?.map((cushion, index) => (
              <div
                key={index}
                className={cn(
                  "tw:col-span-1 tw:px-3 tw:md:px-10 tw:py-5 tw:even:[&>div]:ml-auto tw:odd:!border-r tw:!border-b tw:odd:pr-0 tw:even:pl-0",
                  cushionRecommendations.length === 4 &&
                    "tw:nth-last-[2]:!border-b-0 tw:last:!border-b-0",
                  cushionRecommendations.length === 3 && "tw:last:!border-b-0",
                  cushionRecommendations.length === 2 && "tw:!border-b-0",
                )}
              >
                <div className="tw:w-40">
                  <img
                    src={cushion.imageUrl}
                    alt={cushion.name}
                    className="tw:w-15 tw:mx-auto"
                  />
                  <div className="tw:mt-2">
                    <a href={cushion.url}>
                      <div className="tw:text-base tw:font-medium">
                        {cushion.name}
                      </div>
                    </a>
                    <div className="tw:text-sm tw:text-gray-500 tw:mb-3">
                      {cushion.title}
                    </div>
                    <div
                      className="tw:font-semibold"
                      dangerouslySetInnerHTML={{ __html: cushion.price }}
                    />

                    {cushion.originalPrice &&
                      cushion.originalPrice !== cushion.price && (
                        <s
                          className="tw:text-sm"
                          dangerouslySetInnerHTML={{
                            __html: cushion.originalPrice,
                          }}
                        />
                      )}

                    <button
                      onClick={() => {
                        const existingProduct = widget.products.find(
                          (product) => product.size === cushion.size,
                        );
                        if (existingProduct) {
                          widget.removeProduct(existingProduct.id);
                        }
                        widget.addProduct({
                          id: cushion.id,
                          title: cushion.title,
                          name: cushion.name,
                          url: cushion.url,
                          imageUrl: cushion.imageUrl,
                          size: cushion.size,
                          price: cushion.price,
                          originalPrice: cushion.originalPrice,
                          variant: cushion.variant,
                        });
                        widget.setStep(4);
                      }}
                      className={cn(
                        "tw:flex tw:items-center tw:!mt-2 tw:gap-2 tw:hover:underline tw:cursor-pointer",
                        lang === "ar" && "tw:flex-row-reverse tw:w-full",
                      )}
                    >
                      <div className="tw:rounded-full tw:bg-primary tw:w-4 tw:h-4">
                        <Plus className="tw:w-4 tw:h-4 tw:p-0.5" />
                      </div>
                      <span className="tw:font-semibold">
                        {t("cushion_recommendations.add_for_trial")}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <DrawerFooter>
          <Button
            onClick={() => {
              // remove the products without size
              const existingProduct = widget.products.find(
                (product) => !product.size,
              );
              if (existingProduct) {
                widget.removeProduct(existingProduct.id);
              }

              widget.setStep(4);
            }}
            className="tw:md:px-32 tw:px-20 tw:z-100 tw:!text-sm tw:md:!text-base tw:!h-13.5"
          >
            {t("cushion_recommendations.continue_without_cushions")}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
